import { useEffect, useCallback, useState } from "react";
import {
  Box,
  Flex,
  Text,
  Input,
  Button,
  VStack,
  Image,
  Spinner,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import Delete from "@root/assets/icons/delete-02.svg";
import { EditSvg } from "@root/assets/icons";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@root/assets/icons/CheckIcon";
import { useNoticeTypeContext } from "@root/context/notice-type-context";
import { debounce, generateSlug } from "@root/utils";
import { PAGINATION_DEFAULTS } from "@root/constants";
import { Paginate } from "@root/components/pagination";
import { ModalDelete } from "@root/components/modal-delete";
import SearchInput from "@root/components/common/SearchInput";

export const NoticeCategoryManagement = () => {
  const { t } = useTranslation("common");
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const {
    pageNo,
    setPageNo,
    hoveredId,
    setHoveredId,
    addNewCategory,
    setAddNewCategory,
    newCategory,
    setNewCategory,
    editingId,
    setEditingId,
    editingValue,
    setEditingValue,
    search,
    pageCount,
    pageSize,
    setSearch,
    noticeTypeLoading,
    addNoticeTypeLoading,
    deleteNoticeTypeLoading,
    updateNoticeTypeLoading,
    noticeTypeData,
    addNoticeType,
    fetchNoticeType,
    updateNoticeType,
    deleteNoticeType,
  } = useNoticeTypeContext();

  const handleApiCall = (pageParam: any, searchParam?: any) => {
    fetchNoticeType({
      limit: PAGINATION_DEFAULTS.PAGESIZE,
      offset: (pageParam - 1) * PAGINATION_DEFAULTS.PAGESIZE,
      search: searchParam ? searchParam : search,
    });
  };

  useEffect(() => {
    handleApiCall(pageNo);
  }, []);

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
    handleApiCall(newPage, search);
  };

  const debouncedSearch = useCallback(
    debounce((value: any) => {
      setPageNo(pageNo);
      handleApiCall(pageNo, value);
    }, 500),
    []
  );

  const handleSearch = (e: any) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearch(value);
  };

  const handleAddCategory = () => {
    if (!newCategory?.trim()) return;

    const payload = {
      name: newCategory?.trim(),
      slug: generateSlug(newCategory?.trim()),
    };

    addNoticeType(payload);
  };

  const handleSaveEdit = (cat: any) => {
    const id = cat?.id;
    updateNoticeType(id, {
      name: editingValue,
      slug: generateSlug(editingValue),
    });
  };

  const handleDeleteCategory = () => {
    deleteNoticeType(deleteId);
    setIsDeleteModal(false);
    setDeleteId("");
  };

  const renderCategoryName = (cat: any) => {
    if (editingId === cat?.id) {
      return (
        <>
          <Input
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            size="sm"
            mr={2}
            width={"100%"}
            onClick={(e) => e.stopPropagation()}
          />
          {renderCheckIconUpdate(cat)}
        </>
      );
    } else {
      return (
        <>
          <Text fontWeight={600} flex={1} color={"#2F2F34"}>
            {cat?.name}
          </Text>
          {hoveredId === cat?.id && (
            <>
              <EditSvg
                color={"gray"}
                mr={2}
                title={t("categoryManagement.edit")}
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(cat?.id);
                  setEditingValue(cat?.name);
                }}
                style={{ cursor: "pointer" }}
              />
              {renderDeleteIcon(cat)}
            </>
          )}
        </>
      );
    }
  };

  const renderDeleteIcon = (cat: any) => {
    return (
      <Image
        src={Delete}
        width={5}
        ml={3}
        color="red.400"
        onClick={(e) => {
          e.stopPropagation();
          setIsDeleteModal(true);
          setDeleteId(cat?.id);
        }}
        title={t("categoryManagement.delete")}
        style={{ cursor: "pointer" }}
      />
    );
  };

  const renderCheckIconUpdate = (cat: any) => {
    if (updateNoticeTypeLoading) {
      return <Spinner />;
    }
    return (
      <CheckIcon
        width={6}
        cursor={"pointer"}
        onClick={(e) => {
          e.stopPropagation();
          handleSaveEdit(cat);
        }}
      />
    );
  };

  const renderCheckIcon = () => {
    if (addNoticeTypeLoading) {
      return <Spinner />;
    }
    return (
      <CheckIcon width={6} cursor={"pointer"} onClick={handleAddCategory} />
    );
  };

  const renderAddNewCategory = () => {
    if (addNewCategory) {
      return (
        <Flex gap={2} align="center" mt={4}>
          <Input
            placeholder={t("categoryManagement.enterNoticeCategory")}
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            width={"100%"}
            bg="white"
            borderRadius={"12px"}
            border={"1px solid #CDD6DC"}
          />
          {renderCheckIcon()}

          <Image
            src={Delete}
            width={5}
            ml={3}
            color="red.400"
            cursor={"pointer"}
            onClick={() => setAddNewCategory(false)}
          />
        </Flex>
      );
    }
  };

  const renderCategoriesData = () => {
    if (noticeTypeLoading) {
      return (
        <Flex align="center" justify="center" minH="200px">
          <Spinner size={"lg"} />
        </Flex>
      );
    }
    if (!noticeTypeData?.length) {
      return (
        <Flex align="center" justify="center" minH="200px">
          <Text color="gray.500" textAlign="center" mt={4}>
            {t("categoryManagement.noCategoriesFound")}
          </Text>
        </Flex>
      );
    }

    return (
      <VStack
        align="stretch"
        spacing={1}
        mb={8}
        maxH={"600px"}
        overflowY={"auto"}
      >
        {noticeTypeData?.map((cat: any) => (
          <Box key={cat.id} bg={"transparent"} borderRadius="lg">
            <Flex
              align="center"
              px={2}
              py={2}
              _hover={{ bg: "#1819B414", borderRadius: "12px" }}
              cursor={"pointer"}
              onMouseEnter={() => setHoveredId(cat?.id)}
              onMouseLeave={() => setHoveredId(null)}
            >
              {renderCategoryName(cat)}
            </Flex>
          </Box>
        ))}
      </VStack>
    );
  };

  return (
    <Box bg="#FFFFFF" minH="100vh">
      <SearchInput
        placeholder={t("categoryManagement.searchPlaceholder")}
        width="600px"
       
        value={search}
        onChange={handleSearch}
      />
      <Box mt={6} mb={4}>
        <Button
          leftIcon={<AddIcon />}
          color="#1819B4"
          variant="link"
          fontStyle={"italic"}
          mb={2}
          onClick={() => setAddNewCategory(true)}
        >
          {t("categoryManagement.addCategory")}
        </Button>
        {/* Render Input for add new category */}
        {renderAddNewCategory()}
      </Box>
      {renderCategoriesData()}

      <ModalDelete
        isOpen={isDeleteModal}
        onClose={() => {
          setIsDeleteModal(false);
        }}
        onConfirm={() => handleDeleteCategory()}
        title={t("categoryManagement.removeTitle")}
        description={t("categoryManagement.removeDescription")}
        chatCountDescription={t("categoryManagement.removeConfirm")}
        deleteButtonTitle={t("categoryManagement.Delete")}
        cancelButtonTitle={t("categoryManagement.cancel")}
      />
      <Paginate
        page={pageNo}
        count={pageCount}
        pageSize={PAGINATION_DEFAULTS.PAGESIZE}
        onPageChange={handlePageChange}
        margin={1}
        variant="ghost"
      />
    </Box>
  );
};

export default NoticeCategoryManagement;
